import React, { useState, useRef, useEffect } from 'react';
import { PROSPECTOR_ENGINE_URL } from '../lib/engineUrls';
import {
  Folder,
  Image as ImageIcon,
  Play,
  CheckCircle,
  XCircle,
  Loader,
  Settings,
  FileText,
  AlertCircle,
  Clock,
  Calendar,
  Layers,
  Trash2,
  Edit,
  RefreshCw,
  Eye
} from 'lucide-react';

const NOTION_DATABASE_ID = '2958d96385e080ac9dbef46dd6f4882a';

interface ProcessingResult {
  groupIndex?: number;
  totalGroups?: number;
  imageCount?: number;
  isGrouped?: boolean;
  images?: string[];
  fileName?: string;
  index?: number;
  total?: number;
  status: 'processing' | 'success' | 'error';
  extractedText?: string;
  confidence?: number;
  pageId?: string;
  url?: string;
  error?: string;
  archivePath?: string;
}

interface ScheduledJob {
  id: string;
  interval: string;
  cronExpression: string;
  paused?: boolean;
  options: {
    sourceType: string;
    sourcePath: string;
    notionApiKey?: string;
    notionDatabaseId?: string;
    archiveFolder?: string;
    archiveAlbum?: string;
    enableGrouping?: boolean;
    imageLimit?: string | number;
    groupingMode?: 'OR' | 'AND';
    imageSimilarityThreshold?: number;
  };
  createdAt: string;
  lastRun?: string;
  nextRun?: string;
}

export const BatchImageProcessor: React.FC = () => {
  const [sourceType, setSourceType] = useState<'folder' | 'photos-album'>('folder');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [applePhotosAlbums, setApplePhotosAlbums] = useState<string[]>([]);
  const [, setLoadingAlbums] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState<string>('');
  const [albumImages, setAlbumImages] = useState<Array<{ path: string; name: string; isAlbum?: boolean; count?: number }>>([]);
  const [loadingAlbumImages, setLoadingAlbumImages] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [groupingPreview, setGroupingPreview] = useState<any>(null);
  const [approvedGroups, setApprovedGroups] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<ProcessingResult[]>([]);
  const [archiveFolder, setArchiveFolder] = useState<string>('');
  const [archiveAlbum, setArchiveAlbum] = useState<string>('');
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [enableGrouping, setEnableGrouping] = useState(true);
  const [nameSimilarityThreshold] = useState<number>(0.7);
  const [dateThreshold, setDateThreshold] = useState<number>(5); // minutes
  const [enableFilenamePattern, setEnableFilenamePattern] = useState(true);
  const [enableDateProximity, setEnableDateProximity] = useState(true);
  const [enableImageSimilarity, setEnableImageSimilarity] = useState(true);
  const [imageSimilarityThreshold, setImageSimilarityThreshold] = useState<number>(0.85);
  const [enableFolderStructure, setEnableFolderStructure] = useState(true);
  const [enableFileSize, setEnableFileSize] = useState(true);
  const [groupingMode, setGroupingMode] = useState<'OR' | 'AND'>('OR');
  const [editingJob, setEditingJob] = useState<string | null>(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [currentItem, setCurrentItem] = useState<string>('');
  const [, setProgressSessionId] = useState<string | null>(null);
  const [scheduledJobs, setScheduledJobs] = useState<ScheduledJob[]>([]);
  const [showScheduling, setShowScheduling] = useState(false);
  const [scheduleInterval, setScheduleInterval] = useState<string>('1h');
  const [imageLimit, setImageLimit] = useState<string>('all'); // 'all' or number (for scheduling)
  const [imageLimitValue, setImageLimitValue] = useState<number>(10);
  const [processNowImageLimit, setProcessNowImageLimit] = useState<string>('all'); // 'all' or number (for immediate processing)
  const [processNowImageLimitValue, setProcessNowImageLimitValue] = useState<number>(10);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const progressPollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Poll for progress updates
  const startProgressPolling = (sessionId: string) => {
    console.log('🚀 Starting progress polling for session:', sessionId);
    // Clear any existing interval
    if (progressPollIntervalRef.current) {
      clearInterval(progressPollIntervalRef.current);
    }

    let pollCount = 0;
    // Poll every 1 second
    progressPollIntervalRef.current = setInterval(async () => {
      pollCount++;
      try {
        console.log(`📊 Polling progress (attempt ${pollCount}): ${PROSPECTOR_ENGINE_URL}/api/batch-process/process-batch-album/progress/${sessionId}`);
        const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/batch-process/process-batch-album/progress/${sessionId}`);
        if (response.ok) {
          const data = await response.json();
          console.log('📈 Progress data received:', data);
          if (data.success) {
            setProgress({ current: data.current, total: data.total });
            setCurrentItem(data.currentItem || `Processing... (${data.current}/${data.total})`);

            // If completed, stop polling
            if (data.status === 'completed' || data.status === 'idle') {
              console.log('✅ Processing completed, stopping polling');
              if (progressPollIntervalRef.current) {
                clearInterval(progressPollIntervalRef.current);
                progressPollIntervalRef.current = null;
              }
            }
          }
        } else {
          console.error('Progress poll failed:', response.status);
        }
      } catch (error) {
        console.error('Error polling progress:', error);
      }
    }, 1000);
  };
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (progressPollIntervalRef.current) {
        clearInterval(progressPollIntervalRef.current);
      }
    };
  }, []);

  // Load Apple Photos albums
  const loadApplePhotosAlbums = async () => {
    setLoadingAlbums(true);
    try {
      setApplePhotosAlbums([]); // Clear previous albums while loading
      console.log('Loading Apple Photos albums from:', `${PROSPECTOR_ENGINE_URL}/api/batch-process/apple-photos/albums`);
      
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/batch-process/apple-photos/albums`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Albums response:', data);
      
      if (data.success) {
        const albums = data.albums || [];
        console.log(`Found ${albums.length} albums:`, albums);
        setApplePhotosAlbums(albums);
        
        if (albums.length === 0) {
          if (data.warning) {
            console.warn('Apple Photos:', data.warning);
            // Don't show alert for empty albums, just log it
          } else if (data.error) {
            console.error('Error from server:', data.error);
            alert(`Error loading albums: ${data.error}`);
          }
        }
      } else {
        const errorMsg = data.error || 'Unknown error';
        console.error('Failed to load albums:', errorMsg);
        alert(`Failed to load albums: ${errorMsg}`);
        setApplePhotosAlbums([]);
      }
    } catch (error: any) {
      console.error('Failed to load albums:', error);
      const errorMessage = error.message || 'Unknown error';
      alert(`Failed to connect to Prospector Engine: ${errorMessage}\n\nMake sure it's running on ${PROSPECTOR_ENGINE_URL}`);
      setApplePhotosAlbums([]);
    } finally {
      setLoadingAlbums(false);
    }
  };

  // Load images from selected album
  const loadAlbumImages = async () => {
    if (!selectedAlbum) {
      setAlbumImages([]);
      setLoadingAlbumImages(false);
      return;
    }
    
    // For Apple Photos albums, we don't need to load all images upfront
    // Just mark as ready - the actual processing will fetch images from the album
    // This avoids the slow AppleScript iteration for large albums
    console.log('Album selected:', selectedAlbum);
    
    // Set a placeholder to indicate the album is selected and ready
    // We'll use a special marker to indicate this is an album (not individual files)
    setAlbumImages([{
      path: `album:${selectedAlbum}`,
      name: selectedAlbum,
      isAlbum: true
    }]);
    setLoadingAlbumImages(false);
    
    // Optionally try to get count in background (non-blocking)
    // This is just for display purposes
    setTimeout(async () => {
      try {
        const countResponse = await fetch(`${PROSPECTOR_ENGINE_URL}/api/batch-process/apple-photos/album-images`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ albumName: selectedAlbum, countOnly: true })
        });
        const countData = await countResponse.json();
        
        if (countData.success && countData.count > 0) {
          console.log(`Album "${selectedAlbum}" has ${countData.count} images`);
          // Update with actual count for display
          setAlbumImages([{
            path: `album:${selectedAlbum}`,
            name: selectedAlbum,
            isAlbum: true,
            count: countData.count
          }]);
        }
      } catch (error) {
        console.warn('Could not get album count (non-critical):', error);
        // Don't fail - we can still process without the count
      }
    }, 100);
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files).filter(file => 
        file.type.startsWith('image/')
      );
      setSelectedFiles(files);
    }
  };

  // Preview grouping (for approval workflow)
  const handlePreviewGrouping = async () => {
    // Prevent double-processing
    if (processing) {
      console.warn('Processing already in progress, ignoring duplicate request');
      return;
    }

    // For folder: need selectedFiles
    // For photos-album: need selectedAlbum
    if (sourceType === 'folder' && selectedFiles.length === 0) {
      alert('Please select images to process');
      return;
    }
    if (sourceType === 'photos-album' && !selectedAlbum) {
      alert('Please select an album to process');
      return;
    }

    if (!enableGrouping) {
      // If grouping is disabled, proceed directly to processing
      handleProcess();
      return;
    }

    console.log('Starting preview grouping...', { sourceType, selectedAlbum, selectedFiles: selectedFiles.length });
    setProcessing(true);
    setPreviewMode(true);
    setGroupingPreview(null);

    try {
      let response: Response;
      
      if (sourceType === 'folder' && selectedFiles.length > 0) {
        // For folder: upload files
        let filesToProcess = selectedFiles;
        
        // Apply image limit for preview (same as processing)
        if (processNowImageLimit === 'limit' && processNowImageLimitValue > 0) {
          filesToProcess = filesToProcess.slice(0, processNowImageLimitValue);
          console.log(`Limiting preview to first ${processNowImageLimitValue} images`);
        }

        const formData = new FormData();
        filesToProcess.forEach(file => {
          formData.append('images', file);
        });
        formData.append('enableGrouping', enableGrouping.toString());
        formData.append('nameSimilarityThreshold', nameSimilarityThreshold.toString());
        formData.append('dateThreshold', (dateThreshold * 60 * 1000).toString());
        formData.append('enableFilenamePattern', enableFilenamePattern.toString());
        formData.append('enableDateProximity', enableDateProximity.toString());
        formData.append('enableImageSimilarity', enableImageSimilarity.toString());
        formData.append('imageSimilarityThreshold', imageSimilarityThreshold.toString());
        formData.append('enableFolderStructure', enableFolderStructure.toString());
        formData.append('enableFileSize', enableFileSize.toString());
        formData.append('groupingMode', groupingMode);

        response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/batch-process/preview-grouping`, {
          method: 'POST',
          body: formData
        });
      } else if (sourceType === 'photos-album' && selectedAlbum) {
        // For Apple Photos album: send album name to backend
        response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/batch-process/preview-grouping-album`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            albumName: selectedAlbum,
            enableGrouping,
            nameSimilarityThreshold,
            dateThreshold: dateThreshold * 60 * 1000,
            enableFilenamePattern,
            enableDateProximity,
            enableImageSimilarity,
            imageSimilarityThreshold,
            enableFolderStructure,
            enableFileSize,
            groupingMode,
            imageLimit: processNowImageLimit === 'limit' ? processNowImageLimitValue : 'all'
          })
        });
      } else {
        alert('Please select images or an album to process');
        setProcessing(false);
        setPreviewMode(false);
        return;
      }

      const data = await response.json();
      console.log('🔍 PREVIEW DATA RECEIVED:', JSON.stringify({
        success: data.success,
        groupCount: data.groups?.length,
        singleCount: data.singleImages?.length,
        groups: data.groups?.map((g: any) => ({ id: g.groupId, count: g.imageCount, names: g.images?.map((i: any) => i.name) }))
      }, null, 2));

      if (data.success) {
        setGroupingPreview(data);
        // Auto-approve all groups initially
        const allGroupIds = new Set<string>(data.groups.map((g: any) => g.groupId));
        console.log('✅ Auto-approved groups:', Array.from(allGroupIds));
        setApprovedGroups(allGroupIds);
      } else {
        alert(`Preview failed: ${data.error}`);
        setPreviewMode(false);
      }
    } catch (error: any) {
      console.error('Preview error:', error);
      let errorMessage = error.message || 'Unknown error occurred';
      
      if (error.toString().includes('Load failed') || error.toString().includes('Failed to fetch')) {
        errorMessage = 'Failed to connect to the server. Make sure the Prospector Engine is running (VITE_PROSPECTOR_ENGINE_URL)';
      }
      
      alert(`Error: ${errorMessage}`);
      setPreviewMode(false);
    } finally {
      console.log('Preview complete, resetting processing state');
      setProcessing(false);
    }
  };

  // Process images (after approval)
  const handleProcess = async (approvedGroupIds?: string[]) => {
    console.log('handleProcess called', { 
      approvedGroupIds, 
      sourceType, 
      selectedAlbum, 
      selectedFilesLength: selectedFiles.length,
      previewMode,
      processing 
    });
    
    // Prevent double-processing
    if (processing) {
      console.warn('Processing already in progress, ignoring duplicate request');
      return;
    }

    // For folder: need selectedFiles
    // For photos-album: need selectedAlbum
    if (sourceType === 'folder' && selectedFiles.length === 0) {
      alert('Please select images to process');
      return;
    }
    if (sourceType === 'photos-album' && !selectedAlbum) {
      console.error('No album selected!', { sourceType, selectedAlbum, previewMode });
      alert('Please select an album to process');
      return;
    }

    // API key is optional - backend will use env var if not provided
    console.log('Starting processing...', { sourceType, selectedAlbum, selectedFiles: selectedFiles.length, approvedGroupIds });
    
    // Set processing state BEFORE any async operations
    setProcessing(true);
    setResults([]);
    setProgress({ current: 0, total: 0 });
    setCurrentItem('Starting...');
    setProgressSessionId(null);

    try {
      let response: Response;
      
      if (sourceType === 'folder' && selectedFiles.length > 0) {
        // For folder: upload files
        let filesToProcess = selectedFiles;
        
        // Apply image limit for immediate processing
        if (processNowImageLimit === 'limit' && processNowImageLimitValue > 0) {
          filesToProcess = filesToProcess.slice(0, processNowImageLimitValue);
          console.log(`Limiting processing to first ${processNowImageLimitValue} images`);
        }

        const formData = new FormData();
        filesToProcess.forEach(file => {
          formData.append('images', file);
        });
        formData.append('notionDatabaseId', NOTION_DATABASE_ID);
        if (archiveFolder) formData.append('archiveFolder', archiveFolder);
        if (archiveAlbum) formData.append('archiveAlbum', archiveAlbum);
        formData.append('includeMetadata', includeMetadata.toString());
        formData.append('enableGrouping', enableGrouping.toString());
        formData.append('nameSimilarityThreshold', nameSimilarityThreshold.toString());
        formData.append('dateThreshold', (dateThreshold * 60 * 1000).toString()); // convert to milliseconds
        formData.append('enableFilenamePattern', enableFilenamePattern.toString());
        formData.append('enableDateProximity', enableDateProximity.toString());
        formData.append('enableImageSimilarity', enableImageSimilarity.toString());
        formData.append('imageSimilarityThreshold', imageSimilarityThreshold.toString());
        formData.append('enableFolderStructure', enableFolderStructure.toString());
        formData.append('enableFileSize', enableFileSize.toString());
        formData.append('groupingMode', groupingMode);
        
        // Include approved group IDs if in preview mode and there are approved groups
        if (previewMode && approvedGroupIds && approvedGroupIds.length > 0) {
          formData.append('approvedGroupIds', JSON.stringify(approvedGroupIds));
        }

        response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/batch-process/process-batch`, {
          method: 'POST',
          body: formData
        });
      } else if (sourceType === 'photos-album' && selectedAlbum) {
        // For Apple Photos album: send album name to backend
        const requestBody = {
          albumName: selectedAlbum,
          notionDatabaseId: NOTION_DATABASE_ID,
          archiveFolder: archiveFolder || undefined,
          archiveAlbum: archiveAlbum || undefined,
          includeMetadata,
          enableGrouping,
          nameSimilarityThreshold,
          dateThreshold: dateThreshold * 60 * 1000,
          enableFilenamePattern,
          enableDateProximity,
          enableImageSimilarity,
          imageSimilarityThreshold,
          enableFolderStructure,
          enableFileSize,
          groupingMode,
          imageLimit: processNowImageLimit === 'limit' ? processNowImageLimitValue : 'all',
          approvedGroupIds: previewMode && approvedGroupIds && approvedGroupIds.length > 0 ? approvedGroupIds : undefined
        };
        console.log('Sending process-batch-album request:', { 
          albumName: selectedAlbum, 
          imageLimit: requestBody.imageLimit,
          approvedGroupIds: requestBody.approvedGroupIds,
          previewMode,
          requestBody: JSON.stringify(requestBody, null, 2)
        });
        
        // Create AbortController for timeout (match server timeout of 10 minutes)
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 600000); // 10 minute timeout to match server
        
        try {
          response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/batch-process/process-batch-album`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
            signal: controller.signal
          });
          clearTimeout(timeoutId);
        } catch (fetchError: any) {
          clearTimeout(timeoutId);
          if (fetchError.name === 'AbortError') {
            throw new Error('Request timed out after 10 minutes. The album may be very large. Try using a smaller image limit.');
          }
          // Handle network errors
          if (fetchError.message?.includes('Load failed') || fetchError.message?.includes('network') || fetchError.message?.includes('connection') || fetchError.message?.includes('Failed to fetch')) {
            throw new Error('Network connection lost. The server may have crashed or the connection was interrupted. Please check if the Prospector Engine is running and try again.');
          }
          throw fetchError;
        }
      } else {
        alert('Please select images or an album to process');
        setProcessing(false);
        return;
      }

      console.log('Processing request sent, waiting for response...');
      
      if (!response || !response.ok) {
        if (!response) {
          throw new Error('No response from server. The connection may have been lost or the server crashed. Please check if the Prospector Engine is running.');
        }
        const errorText = await response.text();
        console.error('Response not OK:', response.status, errorText);
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Processing response received:', data);
      
      // Start polling for progress if sessionId is provided
      if (data.sessionId) {
        setProgressSessionId(data.sessionId);
        startProgressPolling(data.sessionId);
      }
      
      if (data.success) {
        // If we have results immediately, use them
        if (data.results && data.results.length > 0) {
          setResults(data.results || []);
          setProgress({ 
            current: data.totalProcessed || data.results.length, 
            total: data.totalProcessed || data.results.length 
          });
        } else if (data.totalProcessed) {
          // Update progress even if no results yet
          setProgress({ 
            current: data.totalProcessed, 
            total: data.totalProcessed 
          });
        }
        setPreviewMode(false);
        setGroupingPreview(null);
        // Show detailed results - use backend counts if available, otherwise calculate
        const results = data.results || [];
        const successfulCount = data.successful !== undefined 
          ? data.successful 
          : results.filter((r: any) => r.status === 'success' || r.status === 'completed').length;
        const failedCount = data.failed !== undefined 
          ? data.failed 
          : results.filter((r: any) => r.status === 'error' || r.status === 'failed').length;
        const skippedResults = results.filter((r: any) => r.status === 'skipped');
        const successfulResults = results.filter((r: any) => r.status === 'success' || r.status === 'completed');
        const failedResults = results.filter((r: any) => r.status === 'error' || r.status === 'failed');
        const noText = results.flatMap((r: any) => r.imagesWithNoText || []);
        
        // Collect all images that were skipped
        const allSkippedImages = new Set<string>();
        
        // Add images from skipped groups
        skippedResults.forEach((r: any) => {
          if (r.images) {
            r.images.forEach((img: string) => allSkippedImages.add(img));
          }
        });
        
        // Add images that had no text (from successful/failed groups that had some images with no text)
        noText.forEach((img: string) => allSkippedImages.add(img));
        
        let message = `✅ PROCESSING COMPLETED!\n\n`;
        message += `📊 Summary:\n`;
        message += `   Total images: ${data.totalProcessed || 0}\n`;
        message += `   ✅ Pages created: ${successfulCount}\n`;
        message += `   ❌ Failed groups: ${failedCount}\n`;
        message += `   ⚠️ Skipped (already processed): ${allSkippedImages.size}\n`;

        if (successfulCount > 0) {
          message += `\n📄 Notion pages created:\n`;
          successfulResults.forEach((r: any) => {
            if (r.url) {
              message += `• ${r.url}\n`;
            } else if (r.pageId) {
              message += `• Page ID: ${r.pageId}\n`;
            }
          });

          message += `\n⚠️ **MANUAL STEP REQUIRED**: Move processed images from "To Process" album to "Processed" album in Apple Photos app.\n`;
          message += `(Photos AppleScript API doesn't support automatic moving between albums)`;
        }
        
        if (failedResults.length > 0) {
          message += `\n❌ Failed groups:\n`;
          failedResults.forEach((r: any) => {
            message += `• ${r.images?.join(', ') || 'Unknown'}: ${r.error || 'Unknown error'}\n`;
          });
        }
        
        if (allSkippedImages.size > 0) {
          message += `\n⏭️ Images skipped (already have Notion pages):\n`;
          Array.from(allSkippedImages).forEach((img: string) => {
            message += `• ${img}\n`;
          });
          message += `\nThese images were skipped because they already have corresponding Notion pages.`;
        }
        
        if (successfulResults.length > 0) {
          message += `\n\nCheck your Notion database to see the created pages.`;
        } else if (allSkippedImages.size > 0) {
          message += `\n\nNo Notion pages were created because no text was extracted from any images.`;
        }
        
        alert(message);
        
        // Also log to console for debugging
        console.log('Processing results:', data);
      } else {
        alert(`Processing failed: ${data.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Processing error:', error);
      let errorMessage = 'Unknown error occurred';
      
      if (error.name === 'AbortError') {
        errorMessage = 'Request timed out. The album may be very large. Try using a smaller image limit.';
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.toString().includes('Load failed') || error.toString().includes('Failed to fetch')) {
        errorMessage = 'Failed to connect to the server. Make sure the Prospector Engine is running (VITE_PROSPECTOR_ENGINE_URL)';
      }
      
      alert(`Error: ${errorMessage}`);
    } finally {
      console.log('Processing complete, resetting state');
      setProcessing(false);
    }
  };

  React.useEffect(() => {
    if (sourceType === 'photos-album') {
      loadApplePhotosAlbums();
    } else {
      // Clear albums when switching away from photos-album
      setApplePhotosAlbums([]);
      setSelectedAlbum('');
      setAlbumImages([]);
    }
  }, [sourceType]);

  React.useEffect(() => {
    if (selectedAlbum) {
      loadAlbumImages();
    } else {
      setAlbumImages([]);
    }
  }, [selectedAlbum]);

  // Load scheduled jobs on mount
  React.useEffect(() => {
    loadScheduledJobs();
  }, []);

  // Auto-reset processing if it gets stuck (safety mechanism)
  React.useEffect(() => {
    if (processing) {
      const timeout = setTimeout(() => {
        console.warn('Processing state has been true for 10 minutes, auto-resetting');
        setProcessing(false);
        setPreviewMode(false);
        setGroupingPreview(null);
      }, 600000); // 10 minute safety timeout
      
      return () => clearTimeout(timeout);
    }
  }, [processing]);

  const loadScheduledJobs = async () => {
    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/batch-process/schedule`);
      const data = await response.json();
      if (data.success) {
        setScheduledJobs(data.jobs || []);
      }
    } catch (error) {
      console.error('Failed to load scheduled jobs:', error);
    }
  };

  const handleSchedule = async () => {
    // For scheduling, we need either:
    // - A folder path (for folder source type)
    // - An album selected (for photos-album source type)
    if (sourceType === 'folder' && selectedFiles.length === 0) {
      alert('Please select a folder or files to schedule processing for');
      return;
    }
    
    if (sourceType === 'photos-album' && !selectedAlbum) {
      alert('Please select an Apple Photos album to schedule processing for');
      return;
    }

    try {
      const sourcePath = sourceType === 'folder' 
        ? 'uploaded-files' // For web uploads, we use a placeholder - in real scenario this would be a folder path
        : selectedAlbum;

      if (!sourcePath) {
        alert('Please select a source (folder or album) to schedule');
        return;
      }

      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/batch-process/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          interval: scheduleInterval,
          sourceType: sourceType === 'folder' ? 'folder' : 'photos-album',
          sourcePath,
          // API key handled by backend from environment variable
          notionDatabaseId: NOTION_DATABASE_ID,
          archiveFolder: archiveFolder || undefined,
          archiveAlbum: archiveAlbum || undefined,
          enableGrouping,
          nameSimilarityThreshold,
          dateThreshold: dateThreshold * 60 * 1000, // convert to milliseconds
          enableFilenamePattern,
          enableDateProximity,
          enableImageSimilarity,
          imageSimilarityThreshold,
          enableFolderStructure,
          enableFileSize,
          groupingMode
        })
      });

      const data = await response.json();
      if (data.success) {
        alert(`Job scheduled successfully! Job ID: ${data.jobId}`);
        loadScheduledJobs();
      } else {
        alert(`Failed to schedule: ${data.error}`);
      }
    } catch (error: any) {
      console.error('Scheduling error:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleRunJobNow = async (jobId: string) => {
    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/batch-process/schedule/${jobId}/run`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        alert('Job executed successfully!');
        loadScheduledJobs();
      } else {
        alert(`Failed to run job: ${data.error}`);
      }
    } catch (error: any) {
      console.error('Run job error:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handlePauseJob = async (jobId: string) => {
    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/batch-process/schedule/${jobId}/pause`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        loadScheduledJobs();
      } else {
        alert(`Failed to pause job: ${data.error}`);
      }
    } catch (error: any) {
      console.error('Pause job error:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleResumeJob = async (jobId: string) => {
    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/batch-process/schedule/${jobId}/resume`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        loadScheduledJobs();
      } else {
        alert(`Failed to resume job: ${data.error}`);
      }
    } catch (error: any) {
      console.error('Resume job error:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleCancelJob = async (jobId: string) => {
    if (!confirm('Are you sure you want to cancel this scheduled job?')) {
      return;
    }

    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/batch-process/schedule/${jobId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        loadScheduledJobs();
      } else {
        alert(`Failed to cancel job: ${data.error}`);
      }
    } catch (error: any) {
      console.error('Cancel job error:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleUpdateJob = async (jobId: string, updates: any) => {
    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/batch-process/schedule/${jobId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      const data = await response.json();
      if (data.success) {
        setEditingJob(null);
        loadScheduledJobs();
      } else {
        alert(`Failed to update job: ${data.error}`);
      }
    } catch (error: any) {
      console.error('Update job error:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const successful = results.filter(r => r.status === 'success').length;
  const failed = results.filter(r => r.status === 'error').length;

  // Job Editor Component
  const JobEditor: React.FC<{ job: ScheduledJob; onSave: (jobId: string, updates: any) => void; onCancel: () => void }> = ({ job, onSave, onCancel }) => {
    const [editInterval, setEditInterval] = useState(job.interval);
    const [editSourcePath, setEditSourcePath] = useState(job.options.sourcePath);
    const [editArchiveFolder, setEditArchiveFolder] = useState(job.options.archiveFolder || '');
    const [editArchiveAlbum, setEditArchiveAlbum] = useState(job.options.archiveAlbum || '');
    const [editEnableGrouping, setEditEnableGrouping] = useState(job.options.enableGrouping !== false);

    return (
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-charcoal-700 mb-1">Interval</label>
          <select
            value={editInterval}
            onChange={(e) => setEditInterval(e.target.value)}
            className="w-full px-2 py-1 border border-[var(--rule)] rounded text-sm"
          >
            <option value="15m">Every 15 minutes</option>
            <option value="30m">Every 30 minutes</option>
            <option value="1h">Every hour</option>
            <option value="3h">Every 3 hours</option>
            <option value="6h">Every 6 hours</option>
            <option value="12h">Every 12 hours</option>
            <option value="24h">Every 24 hours</option>
            <option value="1w">Every week</option>
            <option value="1mo">Every month</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-charcoal-700 mb-1">Source Path</label>
          <input
            type="text"
            value={editSourcePath}
            onChange={(e) => setEditSourcePath(e.target.value)}
            className="w-full px-2 py-1 border border-[var(--rule)] rounded text-sm"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-charcoal-700 mb-1">Archive Folder</label>
            <input
              type="text"
              value={editArchiveFolder}
              onChange={(e) => setEditArchiveFolder(e.target.value)}
              className="w-full px-2 py-1 border border-[var(--rule)] rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-charcoal-700 mb-1">Archive Album</label>
            <input
              type="text"
              value={editArchiveAlbum}
              onChange={(e) => setEditArchiveAlbum(e.target.value)}
              className="w-full px-2 py-1 border border-[var(--rule)] rounded text-sm"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id={`editGrouping-${job.id}`}
            checked={editEnableGrouping}
            onChange={(e) => setEditEnableGrouping(e.target.checked)}
            className="w-4 h-4 text-consulting-slate-blue-600 rounded"
          />
          <label htmlFor={`editGrouping-${job.id}`} className="text-xs text-charcoal-700">
            Enable Grouping
          </label>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onSave(job.id, {
              interval: editInterval,
              sourcePath: editSourcePath,
              archiveFolder: editArchiveFolder || undefined,
              archiveAlbum: editArchiveAlbum || undefined,
              enableGrouping: editEnableGrouping
            })}
            className="px-3 py-1 bg-[var(--green)] text-white rounded text-sm hover:bg-[var(--green)] flex items-center gap-1"
          >
            <CheckCircle className="h-3 w-3" />
            Save
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-1 bg-[var(--slate)] text-white rounded text-sm hover:bg-[var(--bg-raised)] flex items-center gap-1"
          >
            <XCircle className="h-3 w-3" />
            Cancel
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-8 space-y-8">
      {/* Header */}
      <div className="bg-[var(--bg-raised)] p-8 border-2 border-consulting-slate-blue-200">
        <h1 className="text-3xl font-bold text-charcoal-900 mb-2 flex items-center gap-3">
          <ImageIcon className="h-8 w-8 text-consulting-slate-blue-600" />
          Batch Image Processor
        </h1>
        <p className="text-charcoal-600">
          Process images from folders or Apple Photos albums. Extract text, save to Notion, and archive photos.
        </p>
      </div>

      {/* Source Selection */}
      <div className="bg-[var(--bg-raised)] p-6 border-2 border-[var(--rule)]">
        <h2 className="text-xl font-bold text-charcoal-900 mb-4">Source</h2>
        
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => {
              setSourceType('folder');
              setSelectedFiles([]);
              setAlbumImages([]);
            }}
            className={`px-6 py-3  font-medium transition-all ${
              sourceType === 'folder'
                ? 'bg-consulting-slate-blue-600 text-white'
                : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }`}
          >
            <Folder className="h-5 w-5 inline mr-2" />
            Finder Folder
          </button>
          
          <button
            onClick={() => {
              setSourceType('photos-album');
              setSelectedFiles([]);
            }}
            className={`px-6 py-3  font-medium transition-all ${
              sourceType === 'photos-album'
                ? 'bg-consulting-slate-blue-600 text-white'
                : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }`}
          >
            <ImageIcon className="h-5 w-5 inline mr-2" />
            Apple Photos Album
          </button>
        </div>

        {sourceType === 'folder' && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-6 py-3 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors font-medium"
            >
              <Folder className="h-5 w-5 inline mr-2" />
              Select Folder
            </button>
            {selectedFiles.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-charcoal-600 mb-2">
                  {selectedFiles.length} image(s) selected
                </p>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {selectedFiles.map((file, idx) => (
                    <div key={idx} className="text-sm text-charcoal-700 bg-[var(--surface)] p-2 rounded">
                      {file.name} ({(file.size / 1024).toFixed(1)} KB)
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {sourceType === 'photos-album' && (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-charcoal-700">
                  Select Album
                </label>
                <button
                  onClick={loadApplePhotosAlbums}
                  className="text-xs text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 font-medium flex items-center gap-1"
                >
                  <RefreshCw className="h-3 w-3" />
                  Refresh
                </button>
              </div>
              {applePhotosAlbums.length === 0 ? (
                <div className="px-4 py-2 border border-[var(--rule)] bg-[var(--surface)] text-sm text-charcoal-600">
                  <p>No albums found. Click "Refresh" or make sure Photos app is running.</p>
                </div>
              ) : (
                <select
                  value={selectedAlbum}
                  onChange={(e) => setSelectedAlbum(e.target.value)}
                  className="w-full px-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500"
                >
                  <option value="">-- Select an album --</option>
                  {applePhotosAlbums.map(album => (
                    <option key={album} value={album}>{album}</option>
                  ))}
                </select>
              )}
            </div>
            
            {selectedAlbum && (
              <div>
                <p className="text-sm text-charcoal-600 mb-2 font-medium flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-[var(--green)]" />
                  Album "{selectedAlbum}" selected
                </p>
                {albumImages.length > 0 && albumImages[0]?.count && (
                  <p className="text-xs text-charcoal-500 mb-2">
                    {albumImages[0].count.toLocaleString()} image(s) in album (count loaded in background)
                  </p>
                )}
                <p className="text-xs text-charcoal-500 mb-2 bg-[var(--slate-dim)] p-2 rounded">
                  Images will be fetched from the album during processing. Ready to process!
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Configuration */}
      <div className="bg-[var(--bg-raised)] p-6 border-2 border-[var(--rule)]">
        <h2 className="text-xl font-bold text-charcoal-900 mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Configuration
        </h2>
        
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-charcoal-700 mb-2">
                Archive Folder (optional - for file uploads)
              </label>
              <input
                type="text"
                value={archiveFolder}
                onChange={(e) => setArchiveFolder(e.target.value)}
                placeholder="/Users/yourname/archive"
                className="w-full px-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500"
              />
              <p className="text-xs text-charcoal-500 mt-1">
                Full file path where processed images will be copied (e.g., /Users/wesbright/Documents/processed)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-charcoal-700 mb-2">
                Archive Album (optional - for Apple Photos)
              </label>
              <input
                type="text"
                value={archiveAlbum}
                onChange={(e) => setArchiveAlbum(e.target.value)}
                placeholder="Processed"
                className="w-full px-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500"
              />
              <p className="text-xs text-charcoal-500 mt-1">
                Album name in Apple Photos where images will be added after processing (will be created if it doesn't exist)
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="includeMetadata"
              checked={includeMetadata}
              onChange={(e) => setIncludeMetadata(e.target.checked)}
              className="w-4 h-4 text-consulting-slate-blue-600 rounded focus:ring-consulting-slate-blue-500"
            />
            <label htmlFor="includeMetadata" className="text-sm text-charcoal-700">
              Include Apple Photos metadata (Subjects, People, Keywords)
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="enableGrouping"
              checked={enableGrouping}
              onChange={(e) => setEnableGrouping(e.target.checked)}
              className="w-4 h-4 text-consulting-slate-blue-600 rounded focus:ring-consulting-slate-blue-500"
            />
            <label htmlFor="enableGrouping" className="text-sm text-charcoal-700">
              <Layers className="h-4 w-4 inline mr-1" />
              Enable image grouping (combine related images into single Notion pages)
            </label>
          </div>

          {/* Grouping Strategies - only show if grouping is enabled */}
          {enableGrouping && (
            <div className="border-t border-[var(--rule)] pt-4 mt-4">
              <h3 className="text-sm font-semibold text-charcoal-900 mb-3">Grouping Strategies</h3>
              
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-charcoal-700">
                    Grouping Mode
                  </label>
                  <select
                    value={groupingMode}
                    onChange={(e) => setGroupingMode(e.target.value as 'OR' | 'AND')}
                    className="px-3 py-1 border border-[var(--rule)] rounded text-sm"
                  >
                    <option value="OR">OR (any strategy can group)</option>
                    <option value="AND">AND (all enabled strategies must match)</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="enableFilenamePattern"
                      checked={enableFilenamePattern}
                      onChange={(e) => setEnableFilenamePattern(e.target.checked)}
                      className="w-4 h-4 text-consulting-slate-blue-600 rounded"
                    />
                    <label htmlFor="enableFilenamePattern" className="text-sm text-charcoal-700">
                      Filename Pattern (IMG_001, IMG_002, etc.)
                    </label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="enableDateProximity"
                      checked={enableDateProximity}
                      onChange={(e) => setEnableDateProximity(e.target.checked)}
                      className="w-4 h-4 text-consulting-slate-blue-600 rounded"
                    />
                    <label htmlFor="enableDateProximity" className="text-sm text-charcoal-700">
                      Time Difference (images taken within X minutes of each other)
                    </label>
                    {enableDateProximity && (
                      <div className="flex-1 ml-4 flex items-center gap-2">
                        <input
                          type="range"
                          min="1"
                          max="60"
                          value={dateThreshold}
                          onChange={(e) => setDateThreshold(parseInt(e.target.value))}
                          className="flex-1"
                        />
                        <span className="text-xs text-charcoal-500 w-20">{dateThreshold} min</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="enableImageSimilarity"
                      checked={enableImageSimilarity}
                      onChange={(e) => setEnableImageSimilarity(e.target.checked)}
                      className="w-4 h-4 text-consulting-slate-blue-600 rounded"
                    />
                    <label htmlFor="enableImageSimilarity" className="text-sm text-charcoal-700">
                      Image Similarity (removes dissimilar images from time-based groups)
                    </label>
                    {enableImageSimilarity && (
                      <div className="flex-1 ml-4 flex items-center gap-2">
                        <input
                          type="range"
                          min="0.5"
                          max="1.0"
                          step="0.05"
                          value={imageSimilarityThreshold}
                          onChange={(e) => setImageSimilarityThreshold(parseFloat(e.target.value))}
                          className="flex-1"
                        />
                        <span className="text-xs text-charcoal-500 w-20">{(imageSimilarityThreshold * 100).toFixed(0)}%</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="enableFolderStructure"
                      checked={enableFolderStructure}
                      onChange={(e) => setEnableFolderStructure(e.target.checked)}
                      className="w-4 h-4 text-consulting-slate-blue-600 rounded"
                    />
                    <label htmlFor="enableFolderStructure" className="text-sm text-charcoal-700">
                      Folder Structure
                    </label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="enableFileSize"
                      checked={enableFileSize}
                      onChange={(e) => setEnableFileSize(e.target.checked)}
                      className="w-4 h-4 text-consulting-slate-blue-600 rounded"
                    />
                    <label htmlFor="enableFileSize" className="text-sm text-charcoal-700">
                      File Size Similarity
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-[var(--rule)] pt-3">
                <p className="text-xs text-charcoal-500">
                  <strong>OR mode:</strong> Images grouped if they match ANY enabled strategy<br/>
                  <strong>AND mode:</strong> Images grouped only if they match ALL enabled strategies
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scheduling Section */}
      <div className="bg-[var(--bg-raised)] p-6 border-2 border-[var(--rule)]">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-charcoal-900 flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Scheduling
          </h2>
          <button
            onClick={() => setShowScheduling(!showScheduling)}
            className="text-sm text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 font-medium"
          >
            {showScheduling ? 'Hide' : 'Show'} Scheduling
          </button>
        </div>

        {showScheduling && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-charcoal-700 mb-2">
                Schedule Interval
              </label>
              <select
                value={scheduleInterval}
                onChange={(e) => setScheduleInterval(e.target.value)}
                className="w-full px-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500 bg-[var(--bg-raised)]"
              >
                <option value="15m">Every 15 minutes</option>
                <option value="30m">Every 30 minutes</option>
                <option value="1h">Every hour</option>
                <option value="3h">Every 3 hours</option>
                <option value="6h">Every 6 hours</option>
                <option value="12h">Every 12 hours</option>
                <option value="24h">Every 24 hours</option>
                <option value="1w">Every week</option>
                <option value="1mo">Every month</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-charcoal-700 mb-2">
                Image Limit (for testing)
              </label>
              <div className="flex items-center gap-3">
                <select
                  value={imageLimit}
                  onChange={(e) => setImageLimit(e.target.value)}
                  className="px-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500 bg-[var(--bg-raised)]"
                >
                  <option value="all">Process All Images</option>
                  <option value="limit">Limit to N images</option>
                </select>
                {imageLimit === 'limit' && (
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={imageLimitValue}
                    onChange={(e) => setImageLimitValue(parseInt(e.target.value) || 1)}
                    className="px-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500 w-32 bg-[var(--bg-raised)]"
                    placeholder="Number"
                  />
                )}
              </div>
              <p className="text-xs text-charcoal-500 mt-1">
                {imageLimit === 'all' 
                  ? 'All images in the source will be processed each time the job runs'
                  : `Only the first ${imageLimitValue} image(s) will be processed each time (useful for testing)`}
              </p>
            </div>

            <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-3">
              <p className="text-sm text-[var(--text-sec)] mb-2">
                <strong>To schedule a job:</strong>
              </p>
              <ul className="text-sm text-[var(--text-sec)] list-disc list-inside space-y-1">
                {sourceType === 'folder' ? (
                  <li>Select files from a folder (or the folder will be processed when the job runs)</li>
                ) : (
                  <li>Select an Apple Photos album from the dropdown above</li>
                )}
                <li>Choose a schedule interval</li>
                <li>Set image limit (optional, for testing)</li>
                <li>Click "Schedule Processing"</li>
              </ul>
            </div>

            <button
              onClick={handleSchedule}
              disabled={
                (sourceType === 'folder' && selectedFiles.length === 0) ||
                (sourceType === 'photos-album' && !selectedAlbum)
              }
              className="w-full px-6 py-3 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors font-medium disabled:bg-[var(--surface)] disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Calendar className="h-5 w-5" />
              Schedule Processing
              {(sourceType === 'folder' && selectedFiles.length === 0) ||
              (sourceType === 'photos-album' && !selectedAlbum) ? (
                <span className="text-xs ml-2">(Select {sourceType === 'folder' ? 'files' : 'an album'} first)</span>
              ) : null}
            </button>

            {/* Scheduled Jobs List */}
            {scheduledJobs.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Scheduled Jobs</h3>
                <div className="space-y-2">
                  {scheduledJobs.map((job) => (
                    <div key={job.id} className="bg-[var(--surface)] p-4 border border-[var(--rule)]">
                      {editingJob === job.id ? (
                        <JobEditor
                          job={job}
                          onSave={handleUpdateJob}
                          onCancel={() => setEditingJob(null)}
                        />
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-charcoal-900">
                              {job.options.sourceType === 'folder' ? 'Folder' : 'Album'}: {job.options.sourcePath}
                            </div>
                            <div className="text-sm text-charcoal-600">
                              Interval: {job.interval} | Next run: {job.nextRun ? new Date(job.nextRun).toLocaleString() : 'N/A'}
                              {job.lastRun && ` | Last run: ${new Date(job.lastRun).toLocaleString()}`}
                              {job.paused && <span className="ml-2 text-[var(--mustard)] font-medium">(PAUSED)</span>}
                              {job.options.imageLimit && job.options.imageLimit !== 'all' && (
                                <span className="ml-2 text-[var(--text-ter)]">| Limit: {job.options.imageLimit} images</span>
                              )}
                            </div>
                            {job.options.enableGrouping && (
                              <div className="text-xs text-charcoal-500 mt-1">
                                Grouping: {job.options.groupingMode || 'OR'} mode | 
                                Similarity: {job.options.imageSimilarityThreshold ? (job.options.imageSimilarityThreshold * 100).toFixed(0) + '%' : 'N/A'}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {job.paused ? (
                              <button
                                onClick={() => handleResumeJob(job.id)}
                                className="px-3 py-1 bg-[var(--green)] text-white rounded text-sm hover:bg-[var(--green)] flex items-center gap-1"
                                title="Resume job"
                              >
                                <Play className="h-3 w-3" />
                                Resume
                              </button>
                            ) : (
                              <button
                                onClick={() => handlePauseJob(job.id)}
                                className="px-3 py-1 bg-[var(--mustard)] text-white rounded text-sm hover:bg-[var(--mustard)] flex items-center gap-1"
                                title="Pause job"
                              >
                                <Clock className="h-3 w-3" />
                                Pause
                              </button>
                            )}
                            <button
                              onClick={() => setEditingJob(job.id)}
                              className="px-3 py-1 bg-[var(--slate)] text-white rounded text-sm hover:bg-[var(--slate)] flex items-center gap-1"
                            >
                              <Edit className="h-3 w-3" />
                              Edit
                            </button>
                            <button
                              onClick={() => handleRunJobNow(job.id)}
                              className="px-3 py-1 bg-consulting-slate-blue-600 text-white rounded text-sm hover:bg-consulting-slate-blue-700"
                            >
                              Run Now
                            </button>
                            <button
                              onClick={() => handleCancelJob(job.id)}
                              className="px-3 py-1 bg-[var(--coral)] text-white rounded text-sm hover:bg-[var(--coral)]"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Process Now Section */}
      <div className="bg-[var(--bg-raised)] p-6 border-2 border-[var(--rule)]">
        <h2 className="text-xl font-bold text-charcoal-900 mb-4 flex items-center gap-2">
          <Play className="h-5 w-5" />
          Process Images Now
        </h2>
        
        <div className="space-y-4">
          <p className="text-sm text-charcoal-600">
            Process your selected images immediately. {enableGrouping && 'If grouping is enabled, you\'ll see a preview first for grouped images.'}
          </p>
          
          <div>
            <label className="block text-sm font-medium text-charcoal-700 mb-2">
              Image Limit (for testing)
            </label>
            <div className="flex items-center gap-3">
              <select
                value={processNowImageLimit}
                onChange={(e) => setProcessNowImageLimit(e.target.value)}
                className="px-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500 bg-[var(--bg-raised)]"
              >
                <option value="all">Process All Images</option>
                <option value="limit">Limit to N images</option>
              </select>
              {processNowImageLimit === 'limit' && (
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={processNowImageLimitValue}
                  onChange={(e) => setProcessNowImageLimitValue(parseInt(e.target.value) || 1)}
                  className="px-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500 w-32 bg-[var(--bg-raised)]"
                  placeholder="Number"
                />
              )}
            </div>
            <p className="text-xs text-charcoal-500 mt-1">
              {processNowImageLimit === 'all' 
                ? 'All selected images will be processed'
                : `Only the first ${processNowImageLimitValue} image(s) will be processed (useful for testing)`}
            </p>
          </div>
          
          {/* Debug info - remove in production */}
          {process.env.NODE_ENV === 'development' && (
            <div className="text-xs text-[var(--text-ter)] bg-[var(--surface)] p-2 rounded space-y-1">
              <div>Debug: selectedFiles={selectedFiles.length}, albumImages={albumImages.length}, sourceType={sourceType}</div>
              <div>selectedAlbum={selectedAlbum || '(none)'}, loadingAlbumImages={loadingAlbumImages ? 'yes' : 'no'}</div>
              <div>processing={processing ? 'YES' : 'NO'}</div>
              <div>Buttons disabled: {processing || (sourceType === 'folder' ? selectedFiles.length === 0 : (sourceType === 'photos-album' && !selectedAlbum)) ? 'YES' : 'NO'}</div>
            </div>
          )}
          
          <div className="flex gap-3 justify-center">
            {!previewMode ? (
              <>
                {enableGrouping && (
                  <button
                    onClick={async () => {
                      try {
                        await handlePreviewGrouping();
                      } catch (error) {
                        console.error('Preview button error:', error);
                        setProcessing(false);
                      }
                    }}
                    disabled={processing || (sourceType === 'folder' ? selectedFiles.length === 0 : (sourceType === 'photos-album' && !selectedAlbum))}
                    className="px-6 py-3 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors font-medium disabled:bg-[var(--surface)] disabled:cursor-not-allowed flex items-center gap-2"
                    title={sourceType === 'folder' 
                      ? (selectedFiles.length === 0 ? 'Please select files first' : 'Preview grouping')
                      : (selectedAlbum ? (albumImages.length === 0 ? 'Loading album images...' : 'Preview grouping') : 'Please select an album first')}
                  >
                    <Eye className="h-5 w-5" />
                    Preview Grouping First
                  </button>
                )}
                <button
                  onClick={async () => {
                    try {
                      if (enableGrouping) {
                        await handlePreviewGrouping();
                      } else {
                        await handleProcess();
                      }
                    } catch (error) {
                      console.error('Button click error:', error);
                      setProcessing(false);
                    }
                  }}
                  disabled={processing || (sourceType === 'folder' ? selectedFiles.length === 0 : (sourceType === 'photos-album' && !selectedAlbum))}
                  className="px-8 py-4 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors font-medium text-lg disabled:bg-[var(--surface)] disabled:cursor-not-allowed flex items-center gap-2"
                  title={sourceType === 'folder' 
                    ? (selectedFiles.length === 0 ? 'Please select files first' : 'Process images now')
                    : (selectedAlbum ? (albumImages.length === 0 ? 'Loading album images...' : 'Process images now') : 'Please select an album first')}
                >
                  {processing ? (
                    <>
                      <Loader className="h-5 w-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Play className="h-5 w-5" />
                      Process Now
                    </>
                  )}
                </button>
              </>
            ) : groupingPreview && !processing && (
          <div className="w-full space-y-4">
            <div className="bg-[var(--slate-dim)] border-2 border-[var(--slate)] p-6">
              <h3 className="text-lg font-semibold text-[var(--text-sec)] mb-4">Review Grouping Results</h3>
              <p className="text-sm text-[var(--text-sec)] mb-4">
                Single images will be processed automatically. Please review and approve grouped images below.
              </p>
              
              {/* Grouped Images */}
              {groupingPreview.groups && groupingPreview.groups.length > 0 && (
                <div className="space-y-3 mb-4">
                  <h4 className="font-medium text-[var(--text-sec)]">Grouped Images ({groupingPreview.groups.length} groups)</h4>
                  {groupingPreview.groups.map((group: any) => (
                    <div key={group.groupId} className="bg-[var(--bg-raised)] p-4 border-2 border-[var(--slate)]">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="font-medium text-charcoal-900 mb-2">
                            Group {group.groupId.replace('group-', '')} ({group.imageCount} images)
                          </div>
                          <div className="space-y-1">
                            {group.images.map((img: any, idx: number) => (
                              <div key={idx} className="text-sm text-charcoal-700 flex items-center gap-2">
                                <FileText className="h-3 w-3" />
                                {img.name} ({(img.size / 1024).toFixed(1)} KB)
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={approvedGroups.has(group.groupId)}
                            onChange={(e) => {
                              const newApproved = new Set(approvedGroups);
                              if (e.target.checked) {
                                newApproved.add(group.groupId);
                              } else {
                                newApproved.delete(group.groupId);
                              }
                              setApprovedGroups(newApproved);
                            }}
                            className="w-5 h-5 text-consulting-slate-blue-600 rounded"
                          />
                          <span className="text-sm text-charcoal-700">Approve</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Single Images */}
              {groupingPreview.singleImages && groupingPreview.singleImages.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-[var(--text-sec)] mb-2">
                    Single Images ({groupingPreview.singleImages.length}) - Will process automatically
                  </h4>
                  <div className="bg-[var(--bg-raised)] p-3 border border-[var(--slate)] max-h-40 overflow-y-auto">
                    <div className="space-y-1">
                      {groupingPreview.singleImages.map((img: any, idx: number) => (
                        <div key={idx} className="text-sm text-charcoal-700 flex items-center gap-2">
                          <FileText className="h-3 w-3" />
                          {img.name}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setPreviewMode(false);
                    setGroupingPreview(null);
                    setApprovedGroups(new Set());
                  }}
                  className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--bg-raised)] transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    console.log('Process Approved clicked', { 
                      approvedGroups: Array.from(approvedGroups), 
                      approvedGroupsSize: approvedGroups.size,
                      singleImagesCount: groupingPreview.singleImages?.length || 0,
                      sourceType,
                      selectedAlbum,
                      previewMode,
                      processing
                    });
                    if (processing) {
                      console.warn('Already processing, ignoring click');
                      return;
                    }
                    try {
                      // Immediately hide preview and show processing
                      setPreviewMode(false);
                      await handleProcess(Array.from(approvedGroups));
                    } catch (error) {
                      console.error('Error in handleProcess:', error);
                      alert(`Error processing: ${error instanceof Error ? error.message : 'Unknown error'}`);
                    }
                  }}
                  disabled={processing}
                  className="px-6 py-2 bg-[var(--green)] text-white hover:bg-[var(--green)] transition-colors font-medium disabled:bg-[var(--surface)] disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <CheckCircle className="h-5 w-5" />
                  {processing ? 'Processing...' : `Process Approved (${approvedGroups.size} groups + ${groupingPreview.singleImages?.length || 0} singles)`}
                </button>
              </div>
            </div>
          </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress / Loading Indicator - Always show when processing */}
      {processing && (
        <div className="bg-[var(--bg-raised)] p-6 border-2 border-consulting-slate-blue-200 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-consulting-slate-blue-600"></div>
            <h3 className="text-lg font-semibold text-charcoal-900">Processing Images</h3>
          </div>
          <div className="space-y-3">
            {progress.total > 0 ? (
              <>
                <div className="w-full bg-[var(--hover-tint)] rounded-full h-4 mb-2">
                  <div
                    className="bg-consulting-slate-blue-600 h-4 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min((progress.current / progress.total) * 100, 100)}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <p className="text-charcoal-600 font-medium">
                    {progress.current} of {progress.total} processed
                  </p>
                  <p className="text-charcoal-500">
                    {progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}%
                  </p>
                </div>
              </>
            ) : (
              <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
                <p className="text-sm text-charcoal-700">
                  <strong>Processing images...</strong> This may take a few minutes. Please wait.
                </p>
                <p className="text-xs text-charcoal-500 mt-2">
                  Extracting text, analyzing content, and creating Notion pages...
                </p>
              </div>
            )}
            {currentItem && (
              <div className="bg-[var(--surface)] border border-[var(--rule)] p-3">
                <p className="text-xs text-charcoal-600 font-medium mb-1">Current:</p>
                <p className="text-sm text-charcoal-700">{currentItem}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="bg-[var(--bg-raised)] p-6 border-2 border-[var(--rule)]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-charcoal-900">Results</h3>
            <div className="flex gap-4 text-sm">
              <span className="text-[var(--green)] dark:text-[var(--text-ter)] flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                {successful} successful
              </span>
              <span className="text-[var(--coral)] flex items-center gap-1">
                <XCircle className="h-4 w-4" />
                {failed} failed
              </span>
            </div>
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {results.map((result, idx) => (
              <div
                key={idx}
                className={`p-4  border-2 ${
                  result.status === 'success'
                    ? 'border-[var(--green)] bg-[var(--green-dim)]'
                    : result.status === 'error'
                    ? 'border-[var(--coral)] bg-[var(--coral-dim)]'
                    : 'border-[var(--rule)] bg-[var(--surface)]'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {result.status === 'success' && <CheckCircle className="h-5 w-5 text-[var(--green)]" />}
                      {result.status === 'error' && <XCircle className="h-5 w-5 text-[var(--coral)]" />}
                      {result.status === 'processing' && <Loader className="h-5 w-5 text-[var(--text-sec)] animate-spin" />}
                      <span className="font-medium text-charcoal-900">
                        {result.isGrouped ? (
                          <>
                            <Layers className="h-4 w-4 inline mr-1" />
                            Group {result.groupIndex}: {result.imageCount} images
                          </>
                        ) : (
                          result.fileName || result.images?.[0] || 'Unknown'
                        )}
                      </span>
                    </div>
                    
                    {result.isGrouped && result.images && (
                      <div className="mb-2 text-sm text-charcoal-600">
                        Images: {result.images.join(', ')}
                      </div>
                    )}
                    
                    {result.status === 'success' && (
                      <div className="space-y-1 text-sm">
                        {result.extractedText && (
                          <p className="text-charcoal-600">
                            Extracted {result.extractedText.length} characters
                            {result.confidence && ` (${(result.confidence * 100).toFixed(1)}% confidence)`}
                          </p>
                        )}
                        {result.url && (
                          <a
                            href={result.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-consulting-slate-blue-600 hover:underline flex items-center gap-1"
                          >
                            <FileText className="h-4 w-4" />
                            View in Notion
                          </a>
                        )}
                        {result.archivePath && (
                          <p className="text-charcoal-500 text-xs">
                            Archived to: {result.archivePath}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {result.status === 'error' && (
                      <p className="text-[var(--coral)] text-sm">{result.error}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-[var(--slate-dim)] border-2 border-[var(--slate)] p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-[var(--text-ter)] mt-0.5" />
          <div className="flex-1">
            <h4 className="font-semibold text-[var(--text-sec)] mb-2">About This Tool</h4>
            <ul className="text-sm text-[var(--text-sec)] space-y-1 list-disc list-inside">
              <li>Extracts text from images using OCR</li>
              <li>Saves to Notion database "Processed Images"</li>
              <li>Tags images based on Apple Photos metadata (Subjects, People, Keywords)</li>
              <li>Generates AI tags based on extracted content</li>
              <li>Can archive photos to a folder or move to a different album</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

